import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExternalLink, FileText, User, Flame, TrendingUp, MapPin, Users, RefreshCw, Sparkles } from 'lucide-react';
import Topbar from '../components/Topbar';
import Timeline from '../components/Timeline';
import SimilaresVariants from '../components/SimilaresVariants';
import RiskIndicator from '../components/RiskIndicator';
import { RiskAnalysis } from '../types/risk';
import './DetailPage.css';

interface CamaraDetail {
  urlInteiroTeor?: string;
  situacao?: string;
  orgao?: string;
  regime?: string;
  descricaoTipo?: string;
}

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<any>(null);
  const [tramitacoes, setTramitacoes] = useState<any[]>([]);
  const [similares, setSimilares] = useState<any[]>([]);
  const [camaraDetail, setCamaraDetail] = useState<CamaraDetail | null>(null);
  const [activeTab, setActiveTab] = useState('tramitacao');
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [selectedDepth, setSelectedDepth] = useState<'surface' | 'deep'>('surface');
  const [riskAnalysis, setRiskAnalysis] = useState<RiskAnalysis | null>(null);
  const [riskStatus, setRiskStatus] = useState<
    'done' | 'analyzing' | 'failed' | 'queued' | 'pending' | null
  >(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const [propRes, tramRes, simRes] = await Promise.all([
          fetch(`/api/proposicoes/${id}`),
          fetch(`/api/proposicoes/${id}/tramitacoes`),
          fetch(`/api/proposicoes/${id}/similares`),
        ]);

        const propData = await propRes.json();
        const tramData = await tramRes.json();
        const simData = await simRes.json();

        setProposal(propData);
        setTramitacoes(tramData.events || []);
        setSimilares(simData.similares || []);

        // Fetch extra detail from Câmara API if we have external_id
        if (propData.external_id) {
          fetch(`https://dadosabertos.camara.leg.br/api/v2/proposicoes/${propData.external_id}`)
            .then((r) => r.json())
            .then((d) => {
              const dados = d.dados;
              setCamaraDetail({
                urlInteiroTeor: dados.urlInteiroTeor,
                situacao: dados.statusProposicao?.descricaoSituacao,
                orgao: dados.statusProposicao?.siglaOrgao,
                regime: dados.statusProposicao?.regime,
                descricaoTipo: dados.descricaoTipo,
              });
            })
            .catch(() => null);
        }
      } catch (error) {
        console.error('Failed to fetch proposal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Fetch institutional risk analysis (IAL). Falls back to heuristic in the
  // response when the LLM hasn't produced a full analysis yet.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const url = `/api/proposicoes/${id}/risk-analysis?mode=${
      selectedDepth === 'deep' ? 'detailed' : 'bulk'
    }`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setRiskStatus(data.status ?? null);
        if (data.analysis) {
          setRiskAnalysis(data.analysis as RiskAnalysis);
        } else if (data.heuristic_fallback) {
          setRiskAnalysis(data.heuristic_fallback as RiskAnalysis);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, selectedDepth]);

  // Poll risk analysis while it's running. Stops once status flips to done/failed.
  useEffect(() => {
    if (!id || riskStatus !== 'analyzing') return;
    const interval = window.setInterval(() => {
      fetch(
        `/api/proposicoes/${id}/risk-analysis?mode=${
          selectedDepth === 'deep' ? 'detailed' : 'bulk'
        }`,
      )
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          setRiskStatus(data.status ?? null);
          if (data.analysis) setRiskAnalysis(data.analysis as RiskAnalysis);
        })
        .catch(() => {});
    }, 4000);
    return () => window.clearInterval(interval);
  }, [id, riskStatus, selectedDepth]);

  // Poll jabuti analysis when either depth is 'analyzing'
  useEffect(() => {
    if (!id || !proposal) return;
    const polling =
      proposal.jabuti_status === 'analyzing' || proposal.jabuti_deep_status === 'analyzing';
    if (!polling) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/api/proposicoes/${id}`);
        const data = await res.json();
        if (cancelled) return;
        setProposal((prev: any) => ({ ...prev, ...data }));
      } catch (err) {
        console.error('Poll failed:', err);
      }
    };
    const interval = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, proposal?.jabuti_status, proposal?.jabuti_deep_status]);

  if (loading) {
    return (
      <div className="detail-container">
        <Topbar title="Detalhe" />
        <div className="detail-content">
          <div className="loading">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="detail-container">
        <Topbar title="Detalhe" />
        <div className="detail-content">
          <div className="error">Proposição não encontrada</div>
        </div>
      </div>
    );
  }

  const isReal = proposal.sync_source === 'camara' && proposal.external_id;

  const handleReanalyze = async (deep = false) => {
    if (!id || retrying) return;
    setRetrying(true);
    try {
      const endpoint = deep ? 'deep-analyze' : 'retry';
      await fetch(`/api/proposicoes/${id}/jabuti/${endpoint}`, { method: 'POST' });
      if (deep) setSelectedDepth('deep');
      const res = await fetch(`/api/proposicoes/${id}`);
      const data = await res.json();
      setProposal((prev: any) => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Reanalyze failed:', err);
    } finally {
      setRetrying(false);
    }
  };

  // Surface + deep analyses are stored independently; user picks which to display.
  const jabutiSurfaceStatus: string = proposal.jabuti_status || 'pending';
  const jabutiDeepStatus: string = proposal.jabuti_deep_status || 'pending';
  const jabutiSurface = proposal.jabuti;
  const jabutiDeep = proposal.jabuti_deep;
  const hasDeep = jabutiDeepStatus === 'done' && jabutiDeep;
  const effectiveDepth: 'surface' | 'deep' = selectedDepth === 'deep' && hasDeep ? 'deep' : 'surface';
  const jabuti = effectiveDepth === 'deep' ? jabutiDeep : jabutiSurface;
  const jabutiStatus = effectiveDepth === 'deep' ? jabutiDeepStatus : jabutiSurfaceStatus;
  const findings: any[] = Array.isArray(jabuti?.findings) ? jabuti.findings : [];
  const hasJabuti = jabutiStatus === 'done' && jabuti && findings.length > 0;
  const hasHighJabuti = hasJabuti && jabuti.level !== 'low';
  const isAnalyzing = jabutiStatus === 'analyzing';
  const isAnalyzingDeep = jabutiDeepStatus === 'analyzing';
  const analysisFailed = jabutiStatus === 'failed';

  return (
    <div className="detail-container">
      <Topbar title="Detalhe" />
      <div className="detail-content">
        <div className="detail-header">
          <div className="detail-breadcrumb">
            <button onClick={() => navigate(-1)}>← Voltar</button>
          </div>

          <div className="detail-hero">
            <div className="hero-meta">
              <span className={`type-tag type-${proposal.tipo.toLowerCase()}`}>{proposal.tipo}</span>
              <span className="hero-numero">{proposal.numero}/{proposal.ano}</span>
              {isReal ? (
                <span className="hero-source source-real">Câmara #{proposal.external_id}</span>
              ) : (
                <span className="hero-source source-seed">seed</span>
              )}
              {hasHighJabuti && (
                <span
                  className={`hero-jabuti-chip jabuti-${jabuti.level}`}
                  title="Índice de Atenção Legislativa"
                >
                  IAL {Math.round(jabuti.score)}
                </span>
              )}
              {isAnalyzing && (
                <span className="hero-jabuti-chip analyzing">
                  <span className="dot-pulse" /> Analisando proposição…
                </span>
              )}
            </div>

            {(() => {
              const j = jabutiSurface || {};
              const identifier = `${proposal.tipo} ${proposal.numero}/${proposal.ano}`;
              const llmTitle =
                j.headline_com_autor || j.headline || proposal.titulo_llm || '';
              const tituloFinal = llmTitle || identifier;
              return (
                <>
                  <h1 className="hero-title">{tituloFinal}</h1>
                  {proposal.ementa && proposal.ementa !== tituloFinal && (
                    <p className="hero-lede">{proposal.ementa}</p>
                  )}
                </>
              );
            })()}

            {(() => {
              const autores: any[] = Array.isArray(proposal.autores) ? proposal.autores : [];
              const proponentes = autores.filter((a) => a.proponente);
              const principais = proponentes.length > 0 ? proponentes : autores.slice(0, 4);
              if (principais.length === 0 && !proposal.autor) return null;
              if (principais.length === 0) {
                const initial = proposal.autor.trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
                return (
                  <div className="hero-author" title={proposal.autor}>
                    <span className="hero-author-avatar">{initial}</span>
                    <div className="hero-author-text">
                      <span className="hero-author-eyebrow">AUTOR</span>
                      <span className="hero-author-name">{proposal.autor}</span>
                    </div>
                  </div>
                );
              }
              return (
                <div className="hero-authors-list">
                  <span className="hero-author-eyebrow">
                    {proponentes.length > 0 ? 'PROPONENTE' : 'AUTOR'}{principais.length > 1 ? 'ES' : ''} · {autores.length} total
                  </span>
                  <div className="hero-authors-row">
                    {principais.slice(0, 6).map((a, i) => {
                      const init = a.nome.trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
                      return (
                        <div key={i} className="hero-author-chip" title={a.nome}>
                          {a.foto_url ? (
                            <img src={a.foto_url} alt={a.nome} className="hero-author-photo" />
                          ) : (
                            <span className="hero-author-avatar">{init}</span>
                          )}
                          <div className="hero-author-chip-text">
                            <span className="hero-author-chip-name">{a.nome}</span>
                            {a.partido && (
                              <span className="hero-author-chip-party">
                                {a.partido}{a.uf ? `-${a.uf}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {autores.length > 6 && (
                      <span className="hero-authors-more">+{autores.length - 6} signatários</span>
                    )}
                  </div>
                </div>
              );
            })()}

            {(() => {
              const j = jabuti;
              const temas: string[] = Array.isArray(j?.temas) ? j.temas : [];
              const publico: string[] = Array.isArray(j?.publico_alvo) ? j.publico_alvo : [];
              if (temas.length === 0 && publico.length === 0) return null;
              return (
                <div className="hero-chips-block">
                  <div className="hero-chips-eyebrow">TEMAS · CLASSIFICAÇÃO IA</div>
                  <div className="hero-chips">
                    {temas.map((t) => (
                      <span key={`tema-${t}`} className="chip chip-tema">{t}</span>
                    ))}
                    {publico.map((p) => (
                      <span key={`pub-${p}`} className="chip chip-publico" title={`Atinge: ${p}`}>
                        <Users size={11} /> {p}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="hero-info">
              <div className="info-item">
                <span className="info-label">Apresentação</span>
                <span className="info-value mono">
                  {new Date(proposal.data_apresentacao).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {camaraDetail?.orgao && (
                <div className="info-item">
                  <span className="info-label">Órgão atual</span>
                  <span className="info-value mono">{camaraDetail.orgao}</span>
                </div>
              )}
              {camaraDetail?.regime && (
                <div className="info-item">
                  <span className="info-label">Regime</span>
                  <span className="info-value">{camaraDetail.regime}</span>
                </div>
              )}
              {jabuti?.temperatura && (
                <div className="info-item">
                  <span className="info-label"><Flame size={9} /> Temperatura</span>
                  <span className={`info-value class-temp class-temp-${jabuti.temperatura}`} title={jabuti.temperatura_motivo}>
                    {jabuti.temperatura}
                  </span>
                </div>
              )}
              {jabuti?.impacto_economico && (
                <div className="info-item">
                  <span className="info-label"><TrendingUp size={9} /> Impacto</span>
                  <span className={`info-value class-impacto class-impacto-${jabuti.impacto_economico}`}>
                    {jabuti.impacto_economico}
                  </span>
                </div>
              )}
              {jabuti?.alcance && (
                <div className="info-item">
                  <span className="info-label"><MapPin size={9} /> Alcance</span>
                  <span className="info-value">{jabuti.alcance}</span>
                </div>
              )}
            </div>

            {camaraDetail?.situacao && (
              <div className="hero-situacao">
                <span className="situacao-label">Situação atual</span>
                <span className="situacao-text">{camaraDetail.situacao}</span>
              </div>
            )}

            <div className="hero-actions">
              {isReal && (
                <a
                  href={`https://www.camara.leg.br/proposicoesWeb/fichadetramitacao?idProposicao=${proposal.external_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-link"
                >
                  <ExternalLink size={14} />
                  Ficha na Câmara
                </a>
              )}
              {camaraDetail?.urlInteiroTeor && (
                <a
                  href={camaraDetail.urlInteiroTeor}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-link action-link-primary"
                >
                  <FileText size={14} />
                  Texto na íntegra
                </a>
              )}
              <button
                type="button"
                className="action-link action-link-ghost"
                onClick={() => handleReanalyze(false)}
                disabled={retrying || jabutiSurfaceStatus === 'analyzing'}
                title="Re-rodar análise rasa (Haiku)"
              >
                <RefreshCw size={14} className={retrying || jabutiSurfaceStatus === 'analyzing' ? 'spin' : ''} />
                {jabutiSurfaceStatus === 'analyzing' ? 'Analisando…' : 'Reanalisar (rasa)'}
              </button>
              <button
                type="button"
                className="action-link action-link-deep"
                onClick={() => handleReanalyze(true)}
                disabled={retrying || isAnalyzingDeep}
                title="Análise profunda com Sonnet — mais lenta, mais cara, mais nuance"
              >
                <Sparkles size={14} className={isAnalyzingDeep ? 'spin' : ''} />
                {isAnalyzingDeep ? 'Analisando profunda…' : hasDeep ? 'Refazer análise profunda' : 'Análise profunda'}
              </button>
            </div>
          </div>
        </div>

        {/* Institutional risk analysis — multi-dimensional (IAL). */}
        {/* Renders above the legacy jabuti section. Hidden when no data yet. */}
        {riskAnalysis && (
          <section className="risk-analysis-section">
            <RiskIndicator analysis={riskAnalysis} />
            {riskStatus === 'analyzing' && (
              <p className="risk-analysis-pending">
                <span className="dot-pulse" /> Atualizando análise com modelo
                de linguagem… os valores exibidos podem ser preliminares
                (heurística).
              </p>
            )}
            {riskStatus === 'queued' && (
              <p className="risk-analysis-pending">
                <span className="dot-pulse" /> Provedores de IA fora de
                capacidade no momento. Próxima tentativa automática quando
                houver janela; os valores exibidos são preliminares
                (heurística).
              </p>
            )}
          </section>
        )}

        {/*
          As seções abaixo são da pipeline LEGADA jabuti. Quando a nova
          análise multi-dimensional (risk_analysis) já existe, escondemos
          tudo isso — o RiskIndicator acima já cobre todos os estados
          (heurístico + LLM) de forma unificada.
        */}
        {!riskAnalysis && isAnalyzing && (
          <section className="jabuti-report analyzing">
            <div className="jabuti-eyebrow">◈ Análise preliminar · em andamento</div>
            <div className="jabuti-analyzing">
              <span className="dot-pulse" />
              <div>
                <h2 className="jabuti-headline-title">Analisando proposição com IA…</h2>
                <p className="jabuti-lede">
                  Avaliando aderência temática, impacto em direitos, transparência,
                  risco jurídico e complexidade regulatória. Costuma levar alguns segundos.
                </p>
              </div>
            </div>
          </section>
        )}

        {!riskAnalysis && analysisFailed && (
          <section className="jabuti-report failed">
            <div className="jabuti-eyebrow">◈ Análise indisponível no momento</div>
            <p className="jabuti-lede">
              A última tentativa falhou (provedores de IA fora de capacidade
              ou erro transitório). O sistema tenta novamente automaticamente
              quando houver capacidade — ou clique abaixo para forçar agora.
            </p>
            <div className="jabuti-actions" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="action-btn"
                disabled={retrying}
                onClick={() => handleReanalyze(false)}
                title="Resetar status e disparar nova tentativa"
              >
                <RefreshCw size={14} className={retrying ? 'spin' : ''} />
                {retrying ? 'Reanalisando…' : 'Tentar agora'}
              </button>
            </div>
          </section>
        )}

        {/* "Done" sem findings = sem sinais relevantes. */}
        {!riskAnalysis && jabutiStatus === 'done' && !hasJabuti && (
          <section className="jabuti-report ok">
            <div className="jabuti-eyebrow">◉ Sem sinais relevantes</div>
            <p className="jabuti-lede">
              A análise preliminar não identificou indícios de matéria estranha
              ao escopo declarado. Isso não substitui parecer técnico-jurídico;
              indica apenas que as heurísticas semânticas atuais não suscitaram
              alerta. Dimensões avaliadas: aderência temática, impacto em
              direitos, transparência, risco jurídico e complexidade.
            </p>
          </section>
        )}

        {!riskAnalysis && hasJabuti && (
          <section className={`jabuti-report jabuti-${jabuti.level}`}>
            <div className="jabuti-report-head">
              <div className="jabuti-eyebrow-row">
                <div className="jabuti-eyebrow">
                  ◈ Análise preliminar · {
                    effectiveDepth === 'deep' ? 'Claude Sonnet (profunda)' : 'Claude Haiku (rasa)'
                  }
                </div>
                {hasDeep && (
                  <div className="depth-toggle" role="tablist" aria-label="Profundidade da análise">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={effectiveDepth === 'surface'}
                      className={`depth-tab ${effectiveDepth === 'surface' ? 'active' : ''}`}
                      onClick={() => setSelectedDepth('surface')}
                    >
                      Rasa
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={effectiveDepth === 'deep'}
                      className={`depth-tab ${effectiveDepth === 'deep' ? 'active' : ''}`}
                      onClick={() => setSelectedDepth('deep')}
                    >
                      <Sparkles size={11} /> Profunda
                    </button>
                  </div>
                )}
              </div>
              <div className="jabuti-headline">
                <h2 className="jabuti-headline-title">
                  {jabuti.level === 'high'
                    ? 'Sinais relevantes identificados'
                    : 'Sinais de atenção identificados'}
                </h2>
                <div className="jabuti-score-pill" title="Score legado de análise unidimensional. Sucessor: IAL (Índice de Atenção Legislativa).">
                  <span className="jabuti-score-num">{Math.round(jabuti.score)}</span>
                  <span className="jabuti-score-unit">/100</span>
                </div>
              </div>
              <p className="jabuti-lede">
                {jabuti.summary || `${findings.length} ${findings.length === 1 ? 'indicador' : 'indicadores'} na ementa.`}
                {' '}Cada cartão abaixo apresenta o trecho original, sua posição e a justificativa técnica.
              </p>
            </div>

            <ol className="jabuti-findings">
              {findings.map((f, i) => (
                <li key={i} className={`jabuti-finding kind-${f.kind}`}>
                  <div className="finding-locator">
                    <span className="finding-index">{String(i + 1).padStart(2, '0')}</span>
                    <div className="finding-locator-text">
                      <span className="finding-where">
                        {f.sentenceIndex !== null && f.sentenceIndex !== undefined
                          ? `Frase ${f.sentenceIndex + 1}`
                          : 'Ementa'}
                      </span>
                      {f.charStart !== null && f.charStart !== undefined && (
                        <span className="finding-offset">
                          chars {f.charStart}–{f.charEnd}
                        </span>
                      )}
                    </div>
                    <span className={`finding-weight weight-${f.weight >= 20 ? 'hi' : 'med'}`}>
                      +{f.weight}
                    </span>
                  </div>

                  <blockquote className="finding-excerpt">
                    <span className="finding-quote-mark">“</span>
                    {f.excerpt}
                    <span className="finding-quote-mark">”</span>
                  </blockquote>

                  <div className="finding-explanation">
                    <div className="finding-row">
                      <span className="finding-eyebrow">Interpretação</span>
                      <p>{f.interpretation}</p>
                    </div>
                    <div className="finding-row">
                      <span className="finding-eyebrow">Por que destoa</span>
                      <p>{f.why}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            <p className="jabuti-footnote">
              Análise preliminar automatizada baseada em heurísticas semânticas.
              Não substitui parecer jurídico. Confirme com o texto integral
              antes de qualquer decisão editorial.
            </p>
          </section>
        )}

        <div className="detail-tabs">
          <button
            className={`tab-button ${activeTab === 'tramitacao' ? 'active' : ''}`}
            onClick={() => setActiveTab('tramitacao')}
          >
            Tramitação {tramitacoes.length > 0 && `(${tramitacoes.length})`}
          </button>
          <button
            className={`tab-button ${activeTab === 'similares' ? 'active' : ''}`}
            onClick={() => setActiveTab('similares')}
          >
            Similares {similares.length > 0 && `(${similares.length})`}
          </button>
        </div>

        <div className="detail-tab-content">
          {activeTab === 'tramitacao' && (
            <div className="detail-tramitacao-section">
              {tramitacoes.length === 0 ? (
                <div className="empty-tab">Nenhuma tramitação registrada</div>
              ) : (
                <Timeline events={tramitacoes} variant="v1" />
              )}
            </div>
          )}

          {activeTab === 'similares' && (
            <div className="detail-similares-section">
              <SimilaresVariants similares={similares} variant="v1" currentProposicao={proposal} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
